import { useEffect, useMemo, useState } from 'react';

import { Permission, toPersianDigits } from '@bookora/shared';
import {
  Alert,
  Button,
  Checkbox,
  Icon,
  IconButton,
  Input,
  Popover,
  QueryError,
  Select,
  Space,
  Switch,
  Table,
  Typography,
  type ColumnsType,
} from '@bookora/ui';

import type { WorkingHoursEntryDto } from '@/api/generated/model';
import { DirtyNote, TableSkeleton } from '@/components/form-bits';
import { LinkButton } from '@/components/link-button';
import { useUnsavedChangesPrompt } from '@/hooks/use-unsaved-changes-prompt';
import { useCan } from '@/permissions/use-can';

import { scheduleApi, type WorkingHoursRow } from './api';
import { ClockRange } from './clock-range';
import { useSectionMutation, useWorkingHours } from './hooks';
import {
  DEFAULT_WORK_DAYS,
  LUNCH_BREAK,
  TIME_SELECT_PROPS,
  WEEKDAYS,
  quickShiftBlocker,
  weekdayLabel,
  type Weekday,
} from './schedule-bits';
import { SCHEDULE_INVALID_REASON } from './reasons';

import './staff.css';

const { Text } = Typography;

/**
 * The weekly shift grid for one staff member.
 *
 * ── WHY THE WHOLE WEEK IS REPLACED, NOT PATCHED ─────────────────────────────
 *
 * `PUT /staff/:id/working-hours` takes the entire week. That is the right shape
 * for this screen: a schedule is read as a whole («سه‌شنبه‌ها نیست») and edited
 * as a whole, and a per-row PATCH would let a half-applied edit leave a doctor
 * bookable on a day they told the clinic they do not work. The booking engine
 * subtracts these rows from availability, so a wrong row is a patient given an
 * appointment nobody is there for.
 *
 * ── …WHICH IS WHY EVERY FIELD OF A ROW TRAVELS BACK ─────────────────────────
 *
 * A replace writes exactly what it is sent. This grid used to send only day,
 * start, end and «فعال», so saving any change silently erased every lunch
 * break (the calendar's «استراحت ناهار ۱۳–۱۴») and every effective-date window
 * on the week — a doctor's break became bookable the moment somebody moved
 * one of their Tuesdays. Breaks are now edited here, and `effectiveFrom` /
 * `effectiveTo`, which this screen does not edit, are carried through as read.
 *
 * Times are **minutes from midnight**, integers, exactly as the API stores
 * them — no dayjs round-trip, so no timezone can get between the number the
 * doctor picked and the number that decides the slot.
 *
 * ── BUILDING A WEEK SHOULD NOT TAKE FIFTEEN CLICKS ──────────────────────────
 *
 * One shift at a time («افزودن شیفت ▾», pick a day, two times) is how a new
 * doctor ended the day with no shifts and no online bookings. «ساخت سریع
 * برنامه» fills the usual five days in one go, and «کپی این روز به…» repeats a
 * finished day. Neither saves anything: both edit the grid, which is still
 * saved (or abandoned) with the one button at the bottom.
 */

interface ShiftBreak {
  startMinute: number;
  endMinute: number;
  label?: string;
}

interface Shift {
  id: string;
  weekday: Weekday;
  startMinute: number;
  endMinute: number;
  isActive: boolean;
  breaks: ShiftBreak[];
  /** Not edited here — carried through the replace so a save does not clear it. */
  effectiveFrom: string | null;
  effectiveTo: string | null;
}

const MAX_BREAKS = 6;
/** Why «کپی بشه» is blocked in the copy-day popover. */
const NO_COPY_DAY_REASON = 'هنوز روزی انتخاب نشده. اول حداقل یک روز رو انتخاب کنید.';
/** Why «افزودن استراحت» is blocked once a shift has the most breaks it may. */
const BREAKS_FULL_REASON = `هر شیفت حداکثر ${toPersianDigits(MAX_BREAKS)} استراحت داره. برای استراحت جدید، اول یکی از استراحت‌های فعلی رو حذف کنید.`;

function toShifts(rows: readonly WorkingHoursRow[]): Shift[] {
  return rows.map((row) => ({
    id: row.id,
    weekday: row.weekday,
    startMinute: row.startMinute,
    endMinute: row.endMinute,
    isActive: row.isActive,
    breaks: (row.breaks ?? []).map((item) => ({
      startMinute: item.startMinute,
      endMinute: item.endMinute,
      ...(item.label ? { label: item.label } : {}),
    })),
    effectiveFrom: row.effectiveFrom ?? null,
    effectiveTo: row.effectiveTo ?? null,
  }));
}

function sortShifts(shifts: readonly Shift[]): Shift[] {
  const order = new Map(WEEKDAYS.map((day, index) => [day.value, index] as const));
  return [...shifts].sort(
    (a, b) =>
      (order.get(a.weekday) ?? 0) - (order.get(b.weekday) ?? 0) || a.startMinute - b.startMinute,
  );
}

/**
 * Which rows are part of an overlap, so the grid can mark the rows that are
 * wrong instead of only saying that something somewhere is.
 */
function overlappingIds(shifts: readonly Shift[]): Set<string> {
  const ids = new Set<string>();
  for (const day of WEEKDAYS) {
    const onDay = shifts
      .filter((shift) => shift.weekday === day.value && shift.isActive)
      .sort((a, b) => a.startMinute - b.startMinute);
    for (let index = 1; index < onDay.length; index += 1) {
      const previous = onDay[index - 1];
      const current = onDay[index];
      if (previous && current && current.startMinute < previous.endMinute) {
        ids.add(previous.id);
        ids.add(current.id);
      }
    }
  }
  return ids;
}

/** A break must sit inside its shift, end after it starts, and not overlap another break. */
function breaksInvalid(shift: Shift): boolean {
  const sorted = [...shift.breaks].sort((a, b) => a.startMinute - b.startMinute);
  return sorted.some(
    (item, index) =>
      item.endMinute <= item.startMinute ||
      item.startMinute < shift.startMinute ||
      item.endMinute > shift.endMinute ||
      (index > 0 && item.startMinute < (sorted[index - 1]?.endMinute ?? 0)),
  );
}

let freshCounter = 0;
function freshId(weekday: Weekday): string {
  freshCounter += 1;
  return `new-${weekday}-${freshCounter}-${Date.now()}`;
}

export interface WorkingHoursEditorProps {
  staffId: string;
  /** Shown above the grid — usually the colleague's name. */
  caption?: string;
  /**
   * The branch whose roster this grid edits (a multi-branch clinic). The read
   * and the replace are both scoped to it: saving branch A's week never
   * touches the colleague's shifts at branch B. `null`/absent = the clinic's
   * only branch (the server resolves it).
   */
  branchId?: string | null;
}

export function WorkingHoursEditor({ staffId, caption, branchId = null }: WorkingHoursEditorProps) {
  const canEdit = useCan(Permission.SETTINGS_EDIT);
  const query = useWorkingHours(staffId, branchId);

  const [shifts, setShifts] = useState<Shift[]>([]);
  const [dirty, setDirty] = useState(false);
  /** Rows added in this editing session; they fade in where they land. */
  const [freshIds, setFreshIds] = useState<ReadonlySet<string>>(() => new Set());
  /** Rows whose break editor is open. */
  const [expanded, setExpanded] = useState<readonly string[]>([]);
  useUnsavedChangesPrompt(dirty);

  // A sibling mutation in the section (deleting a time-off row below this
  // grid) invalidates `['staff-section', …]`, which refetches this query. Only
  // an UNEDITED grid follows the server; three edited shifts must not vanish
  // because a colleague's holiday was removed.
  useEffect(() => {
    if (query.data && !dirty) {
      setShifts(sortShifts(toShifts(query.data)));
    }
    // `dirty` is deliberately not a dependency: flipping it must not re-run
    // the sync, only the next server answer should.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data]);

  const save = useSectionMutation(
    (entries: WorkingHoursEntryDto[]) =>
      scheduleApi.replaceWorkingHours(staffId, { entries }, branchId),
    { successMessage: 'برنامهٔ هفتگی ذخیره شد.', onDone: () => setDirty(false) },
  );

  const invalidIds = useMemo(() => {
    const ids = overlappingIds(shifts);
    for (const shift of shifts) {
      if (shift.endMinute <= shift.startMinute || breaksInvalid(shift)) ids.add(shift.id);
    }
    return ids;
  }, [shifts]);
  const invalid = invalidIds.size > 0;

  function replaceShifts(next: Shift[], added: readonly string[] = []): void {
    setShifts(sortShifts(next));
    if (added.length > 0) {
      setFreshIds((current) => {
        const copy = new Set(current);
        for (const id of added) copy.add(id);
        return copy;
      });
    }
    setDirty(true);
  }

  function update(id: string, patch: Partial<Shift>): void {
    replaceShifts(shifts.map((shift) => (shift.id === id ? { ...shift, ...patch } : shift)));
  }

  function addShift(weekday: Weekday): void {
    const id = freshId(weekday);
    replaceShifts(
      [
        ...shifts,
        {
          id,
          weekday,
          startMinute: 9 * 60,
          endMinute: 17 * 60,
          isActive: true,
          breaks: [],
          effectiveFrom: null,
          effectiveTo: null,
        },
      ],
      [id],
    );
  }

  function removeShift(id: string): void {
    replaceShifts(shifts.filter((shift) => shift.id !== id));
  }

  /**
   * Gives each chosen day exactly the given shifts, replacing what it had.
   * Replace rather than add: «همهٔ روزها ۹ تا ۱۷» on a day that already had
   * 9–17 would otherwise produce two overlapping copies to delete by hand.
   */
  function setDays(
    days: readonly Weekday[],
    template: ReadonlyArray<Pick<Shift, 'startMinute' | 'endMinute' | 'isActive' | 'breaks'>>,
  ): void {
    const targets = new Set(days);
    const kept = shifts.filter((shift) => !targets.has(shift.weekday));
    const added: Shift[] = [];
    for (const day of days) {
      for (const item of template) {
        added.push({
          id: freshId(day),
          weekday: day,
          startMinute: item.startMinute,
          endMinute: item.endMinute,
          isActive: item.isActive,
          breaks: item.breaks.map((b) => ({ ...b })),
          effectiveFrom: null,
          effectiveTo: null,
        });
      }
    }
    replaceShifts(
      [...kept, ...added],
      added.map((shift) => shift.id),
    );
  }

  function toggleExpanded(id: string): void {
    setExpanded((current) =>
      current.includes(id) ? current.filter((key) => key !== id) : [...current, id],
    );
  }

  if (query.isLoading) return <TableSkeleton rows={5} label="در حال بارگذاری شیفت‌ها…" />;
  if (query.error) return <QueryError error={query.error} onRetry={() => void query.refetch()} />;

  const daysWithShifts = new Set(shifts.map((shift) => shift.weekday));

  const columns: ColumnsType<Shift> = [
    {
      title: 'روز',
      dataIndex: 'weekday',
      width: 110,
      render: (weekday: Weekday) => weekdayLabel(weekday),
    },
    // An editor gets the two pickers; a reader gets the one range they read.
    ...(canEdit
      ? [
          {
            title: 'شروع',
            dataIndex: 'startMinute',
            width: 120,
            render: (minute: number, shift: Shift) => (
              <Select
                {...TIME_SELECT_PROPS}
                aria-label={`شروع شیفت ${weekdayLabel(shift.weekday)}`}
                value={minute}
                onChange={(value: number) => update(shift.id, { startMinute: value })}
              />
            ),
          },
          {
            title: 'پایان',
            dataIndex: 'endMinute',
            width: 120,
            render: (minute: number, shift: Shift) => (
              <Select
                {...TIME_SELECT_PROPS}
                aria-label={`پایان شیفت ${weekdayLabel(shift.weekday)}`}
                value={minute}
                onChange={(value: number) => update(shift.id, { endMinute: value })}
              />
            ),
          },
        ]
      : [
          {
            title: 'ساعت',
            key: 'range',
            width: 160,
            render: (_: unknown, shift: Shift) => (
              <ClockRange start={shift.startMinute} end={shift.endMinute} />
            ),
          },
        ]),
    {
      title: 'استراحت',
      key: 'breaks',
      render: (_, shift) => (
        <Space size={4} wrap>
          {shift.breaks.length === 0 ? (
            <Text type="secondary">بدون استراحت</Text>
          ) : (
            shift.breaks.map((item, index) => (
              <Text key={index} className="bk-shift-break-chip">
                {item.label ? `${item.label} ` : ''}
                <ClockRange start={item.startMinute} end={item.endMinute} />
              </Text>
            ))
          )}
          {canEdit ? (
            <Button
              size="small"
              type="link"
              aria-expanded={expanded.includes(shift.id)}
              aria-label={`${expanded.includes(shift.id) ? 'بستن' : 'ویرایش'} استراحت‌های ${weekdayLabel(shift.weekday)}`}
              onClick={() => toggleExpanded(shift.id)}
            >
              {expanded.includes(shift.id) ? 'بستن' : 'ویرایش'}
            </Button>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'وضعیت',
      dataIndex: 'isActive',
      width: 100,
      render: (isActive: boolean, shift) => (
        // Worded both ways: a bare track read as "off" in RTL whichever way
        // it was, and «فعال» as the column title did not say which state
        // the dark track was.
        <Switch
          checked={isActive}
          disabled={!canEdit}
          checkedChildren="فعال"
          unCheckedChildren="خاموش"
          aria-label={`فعال بودن شیفت ${weekdayLabel(shift.weekday)}`}
          onChange={(value) => update(shift.id, { isActive: value })}
        />
      ),
    },
    ...(canEdit
      ? [
          {
            title: '',
            key: 'actions',
            width: 120,
            render: (_: unknown, shift: Shift) => (
              <Space size={2}>
                <CopyDayButton
                  weekday={shift.weekday}
                  daysWithShifts={daysWithShifts}
                  onCopy={(days) =>
                    setDays(
                      days,
                      shifts.filter((item) => item.weekday === shift.weekday),
                    )
                  }
                />
                <IconButton
                  type="text"
                  danger
                  name="trash"
                  label={`حذف شیفت ${weekdayLabel(shift.weekday)}`}
                  onClick={() => removeShift(shift.id)}
                />
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <Space orientation="vertical" size={12} style={{ width: '100%' }}>
      {caption ? <Text type="secondary">{caption}</Text> : null}

      {!canEdit ? (
        <Alert
          type="info"
          showIcon
          title="برای تغییر ساعت کاری، دسترسی «ویرایش تنظیمات» لازم است."
        />
      ) : null}

      {canEdit ? (
        <Space wrap>
          <QuickWeekBuilder
            daysWithShifts={daysWithShifts}
            onApply={(days, template) => setDays(days, [template])}
          />
          {/* One «افزودن شیفت» with the day as a choice, instead of seven
              «+ شنبه» buttons with a typed plus sign. */}
          <Select<Weekday>
            aria-label="افزودن شیفت برای یک روز"
            placeholder={
              <Space size={6}>
                <Icon name="add" size={16} />
                افزودن شیفت برای یک روز
              </Space>
            }
            value={null}
            style={{ minInlineSize: 220 }}
            options={WEEKDAYS.map((day) => ({ value: day.value, label: day.label }))}
            onChange={(weekday) => addShift(weekday)}
          />
          {/* The clinic-wide version of the quick builder: one shift on the
              chosen days for several colleagues at once. It is named after
              the dialog it opens, not «همین ساعت…»: nothing from this grid
              is carried over, and a label promising "these hours" would have
              the user expect their edits here to be waiting there. Only this
              colleague arrives ticked there; the others are the user's choice. */}
          <LinkButton
            type="link"
            to={`/settings?tab=hours&bulk=1&staff=${encodeURIComponent(staffId)}`}
          >
            یک برنامه برای چند کارمند
          </LinkButton>
        </Space>
      ) : null}

      {invalid ? (
        <Alert
          className="bk-enter-fade"
          type="error"
          showIcon
          title={`${toPersianDigits(invalidIds.size)} شیفت رو باید اصلاح کنید`}
          description="ردیف‌های رنگی رو ببینید: پایان هر شیفت باید بعد از شروع اون باشه، دو شیفت در یک روز نباید تداخل داشته باشن و استراحت باید داخل ساعت شیفت باشه."
        />
      ) : null}

      <Table<Shift>
        rowKey="id"
        pagination={false}
        dataSource={shifts}
        columns={columns}
        scroll={{ x: 760 }}
        expandable={{
          expandedRowKeys: expanded,
          showExpandColumn: false,
          expandedRowRender: (shift) => (
            <BreakEditor shift={shift} onChange={(breaks) => update(shift.id, { breaks })} />
          ),
        }}
        rowClassName={(shift) =>
          [
            invalidIds.has(shift.id) ? 'bk-shift-invalid' : '',
            freshIds.has(shift.id) ? 'bk-enter-fade' : '',
          ]
            .filter(Boolean)
            .join(' ')
        }
        locale={{
          emptyText: canEdit
            ? 'برای این کارمند شیفتی ثبت نشده و امکان رزرو نوبت در تقویم وجود نداره. با «ساخت سریع برنامه» روزهای کاری‌اش رو یک‌جا بسازید.'
            : 'برای این کارمند شیفتی ثبت نشده و امکان رزرو نوبت در تقویم وجود نداره.',
        }}
      />

      {canEdit ? (
        <div className="bk-form-actions">
          <Button
            type="primary"
            // Unchanged: plainly disabled (the DirtyNote is the convention).
            // Changed but invalid: the button says why, by focus and tap too.
            disabled={!dirty}
            disabledReason={dirty && invalid ? SCHEDULE_INVALID_REASON : null}
            loading={save.isPending}
            onClick={() =>
              save.mutate(
                shifts.map((shift) => ({
                  weekday: shift.weekday,
                  startMinute: shift.startMinute,
                  endMinute: shift.endMinute,
                  isActive: shift.isActive,
                  breaks: shift.breaks,
                  effectiveFrom: shift.effectiveFrom,
                  effectiveTo: shift.effectiveTo,
                })),
              )
            }
          >
            ذخیرهٔ برنامهٔ هفتگی
          </Button>
          {/* Not «N شیفت ذخیره‌نشده»: N was the TOTAL, so editing one shift
              of twelve claimed twelve were unsaved. */}
          <DirtyNote
            dirty={dirty}
            dirtyText={invalid ? 'اول ردیف‌های رنگی رو اصلاح کنید' : 'تغییرات ذخیره نشده'}
            cleanText={null}
          />
        </div>
      ) : null}
    </Space>
  );
}

/* -------------------------------------------------------------------------- */
/* The quick builder                                                          */
/* -------------------------------------------------------------------------- */

function QuickWeekBuilder({
  daysWithShifts,
  onApply,
}: {
  daysWithShifts: ReadonlySet<Weekday>;
  onApply: (
    days: Weekday[],
    template: Pick<Shift, 'startMinute' | 'endMinute' | 'isActive' | 'breaks'>,
  ) => void;
}) {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState<Weekday[]>([...DEFAULT_WORK_DAYS]);
  const [start, setStart] = useState(9 * 60);
  const [end, setEnd] = useState(17 * 60);
  const [lunch, setLunch] = useState(false);

  const replacing = days.filter((day) => daysWithShifts.has(day));
  // Every way this can be stopped is said — on the button (focus, tap) and as
  // the line above it — not only an end before the start.
  const blocker = quickShiftBlocker({ days, start, end, lunch });

  const content = (
    <Space orientation="vertical" size={10} style={{ maxInlineSize: 340 }}>
      <fieldset style={{ margin: 0, padding: 0, border: 0 }}>
        <legend className="bk-field-label" style={{ padding: 0 }}>
          روزهای کاری
        </legend>
        <Checkbox.Group<Weekday>
          value={days}
          onChange={(next) => setDays(next)}
          options={WEEKDAYS.map((day) => ({ value: day.value, label: day.label }))}
        />
      </fieldset>
      <Space size={8} align="end" wrap>
        <div style={{ inlineSize: 120 }}>
          <span className="bk-field-label">از ساعت</span>
          <Select
            {...TIME_SELECT_PROPS}
            aria-label="شروع شیفت‌ها"
            value={start}
            onChange={setStart}
          />
        </div>
        <div style={{ inlineSize: 120 }}>
          <span className="bk-field-label">تا ساعت</span>
          <Select {...TIME_SELECT_PROPS} aria-label="پایان شیفت‌ها" value={end} onChange={setEnd} />
        </div>
      </Space>
      <Checkbox checked={lunch} onChange={(event) => setLunch(event.target.checked)}>
        استراحت ناهار <ClockRange start={LUNCH_BREAK.startMinute} end={LUNCH_BREAK.endMinute} />
      </Checkbox>
      {replacing.length > 0 ? (
        <Text type="secondary" style={{ fontSize: 'var(--bk-text-caption)' }}>
          شیفت‌های فعلی {replacing.map(weekdayLabel).join('، ')} با این ساعت جایگزین می‌شه.
        </Text>
      ) : null}
      {blocker ? (
        <Text
          type="danger"
          className="bk-feedback-enter"
          role="status"
          style={{ fontSize: 'var(--bk-text-caption)' }}
        >
          {blocker}
        </Text>
      ) : null}
      <Button
        type="primary"
        disabledReason={blocker}
        onClick={() => {
          onApply(days, {
            startMinute: start,
            endMinute: end,
            isActive: true,
            breaks: lunch ? [{ ...LUNCH_BREAK }] : [],
          });
          setOpen(false);
        }}
      >
        افزودن به برنامه ({toPersianDigits(days.length)} روز)
      </Button>
      <Text type="secondary" style={{ fontSize: 'var(--bk-text-caption)' }}>
        هنوز چیزی ذخیره نمی‌شه؛ بعد از بررسی، «ذخیرهٔ برنامهٔ هفتگی» رو بزنید.
      </Text>
    </Space>
  );

  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      title="ساخت سریع برنامهٔ هفتگی"
      content={content}
    >
      <Button type="primary" icon={<Icon name="calendar" size={16} />}>
        ساخت سریع برنامه
      </Button>
    </Popover>
  );
}

/* -------------------------------------------------------------------------- */

function CopyDayButton({
  weekday,
  daysWithShifts,
  onCopy,
}: {
  weekday: Weekday;
  daysWithShifts: ReadonlySet<Weekday>;
  onCopy: (days: Weekday[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState<Weekday[]>([]);
  const others = WEEKDAYS.filter((day) => day.value !== weekday);
  const replacing = days.filter((day) => daysWithShifts.has(day));

  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setDays([]);
      }}
      title={`کپی شیفت‌های ${weekdayLabel(weekday)} به…`}
      content={
        <Space orientation="vertical" size={10} style={{ maxInlineSize: 320 }}>
          <Checkbox.Group<Weekday>
            value={days}
            onChange={(next) => setDays(next)}
            options={others.map((day) => ({ value: day.value, label: day.label }))}
          />
          {replacing.length > 0 ? (
            <Text type="secondary" style={{ fontSize: 'var(--bk-text-caption)' }}>
              شیفت‌های فعلی {replacing.map(weekdayLabel).join('، ')} جایگزین می‌شه.
            </Text>
          ) : null}
          <Button
            type="primary"
            size="small"
            disabledReason={days.length === 0 ? NO_COPY_DAY_REASON : null}
            onClick={() => {
              onCopy(days);
              setOpen(false);
              setDays([]);
            }}
          >
            کپی بشه
          </Button>
        </Space>
      }
    >
      <IconButton
        type="text"
        name="copy"
        label={`کپی شیفت‌های ${weekdayLabel(weekday)} به روزهای دیگه`}
        tooltip="کپی به روزهای دیگه"
      />
    </Popover>
  );
}

/* -------------------------------------------------------------------------- */

function BreakEditor({
  shift,
  onChange,
}: {
  shift: Shift;
  onChange: (breaks: ShiftBreak[]) => void;
}) {
  function patch(index: number, next: Partial<ShiftBreak>): void {
    onChange(shift.breaks.map((item, i) => (i === index ? { ...item, ...next } : item)));
  }

  return (
    <Space orientation="vertical" size={8} style={{ width: '100%' }}>
      <Text type="secondary" style={{ fontSize: 'var(--bk-text-caption)' }}>
        در ساعت استراحت برای این کارمند نوبت داده نمی‌شه. استراحت باید داخل ساعت شیفت (
        <ClockRange start={shift.startMinute} end={shift.endMinute} />) باشه.
      </Text>
      {shift.breaks.map((item, index) => {
        const wrong =
          item.endMinute <= item.startMinute ||
          item.startMinute < shift.startMinute ||
          item.endMinute > shift.endMinute;
        return (
          <Space key={index} size={8} wrap align="center" className="bk-enter-fade">
            <Input
              aria-label={`عنوان استراحت ${toPersianDigits(index + 1)}`}
              placeholder="مثلاً ناهار"
              maxLength={60}
              style={{ inlineSize: 140 }}
              value={item.label ?? ''}
              onChange={(event) => patch(index, { label: event.target.value || undefined })}
            />
            <div style={{ inlineSize: 110 }}>
              <Select
                {...TIME_SELECT_PROPS}
                status={wrong ? 'error' : undefined}
                aria-label={`شروع استراحت ${toPersianDigits(index + 1)}`}
                value={item.startMinute}
                onChange={(value: number) => patch(index, { startMinute: value })}
              />
            </div>
            <div style={{ inlineSize: 110 }}>
              <Select
                {...TIME_SELECT_PROPS}
                status={wrong ? 'error' : undefined}
                aria-label={`پایان استراحت ${toPersianDigits(index + 1)}`}
                value={item.endMinute}
                onChange={(value: number) => patch(index, { endMinute: value })}
              />
            </div>
            <IconButton
              type="text"
              danger
              name="trash"
              label={`حذف استراحت ${toPersianDigits(index + 1)}`}
              onClick={() => onChange(shift.breaks.filter((_, i) => i !== index))}
            />
          </Space>
        );
      })}
      <Button
        size="small"
        icon={<Icon name="add" size={16} />}
        style={{ alignSelf: 'flex-start' }}
        disabledReason={shift.breaks.length >= MAX_BREAKS ? BREAKS_FULL_REASON : null}
        onClick={() => {
          // A sensible first guess: an hour in the middle of the shift,
          // snapped to the quarter hour, which the user then adjusts.
          const middle = Math.floor((shift.startMinute + shift.endMinute) / 2 / 15) * 15;
          const startMinute = Math.max(shift.startMinute, Math.min(middle, shift.endMinute - 60));
          onChange([
            ...shift.breaks,
            { startMinute, endMinute: Math.min(startMinute + 60, shift.endMinute) },
          ]);
        }}
      >
        افزودن استراحت
      </Button>
    </Space>
  );
}
